import { useState } from 'react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';

import { alertService } from '../../../../shared/services/alertService';
import { usePostNewPriorityMutation } from '../../counselling.services';
import { usePlanningWizard } from '../PlanningWizardContext';
import { PriorityEntry } from '../types';

/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * Step 6 — Priority ordering (Angular addOneMorePriority / removePriorityList /
 * addNewPriority / prioritySubmit). The list has a trailing blank row (label
 * === '') that renders the add controls; each committed row shows its order
 * number and a remove icon. Removals are tracked in removeList for the diff
 * that priorityApiSubmit posts at final submit.
 */
const StepPriority = () => {
  const {
    priorityList,
    setPriorityList,
    allPriorityList,
    addedPriorityList,
    setAddedPriorityList,
    setRemoveList,
    counsellingId,
    refreshPriorityMaster,
    nextPage,
    previousPage,
  } = usePlanningWizard();

  const [postNewPriority] = usePostNewPriorityMutation();
  const [priority, setPriority] = useState('');
  const [priorityModal, setPriorityModal] = useState(false);
  const [newPriority, setNewPriority] = useState('');
  const [prioritySubmitted, setPrioritySubmitted] = useState(false);

  const addOneMorePriority = (ind: number) => {
    if (!priority) {
      alertService.error('Please select a priority');
      return;
    }
    const fil = priorityList.filter((ft) => ft.value === priority);
    if (fil.length > 0) {
      setAddedPriorityList((prev) => {
        const next = prev.map((row, i) =>
          i === ind ? { ...row, label: fil[0].label, value: fil[0].value } : row
        );
        return [
          ...next,
          { label: '', value: '', IndexId: '', IsActive: '', PriorityNo: '' },
        ];
      });
    }
    setPriorityList((prev) => prev.filter((obj) => obj.value !== priority));
    setPriority('');
  };

  const removePriorityList = (ind: number, obj: PriorityEntry) => {
    setAddedPriorityList((prev) => prev.filter((_, i) => i !== ind));
    const add = allPriorityList.filter((ft) => ft.value === obj.value);
    if (add.length > 0) setPriorityList((prev) => [...prev, add[0]]);
    setRemoveList((prev) => [
      ...prev,
      {
        CnsId: counsellingId,
        IndexId: obj.IndexId,
        IsActive: 0,
        PriorityId: obj.value,
        PriorityNo: obj.PriorityNo,
      },
    ]);
  };

  const handleAddNewPriority = async () => {
    setPrioritySubmitted(true);
    const trimmed = newPriority.trim();
    if (!trimmed) {
      return;
    }
    try {
      const res: any = await postNewPriority({
        records: { Priority: trimmed, IndxId: '' },
      }).unwrap();
      if (res?.dataStatus) {
        alertService.success('New priority added successfully');
        setPrioritySubmitted(false);
        setPriorityModal(false);
        refreshPriorityMaster();
      }
    } catch {
      alertService.error('Something went wrong. Please try again');
    }
  };

  const prioritySubmit = () => {
    if (addedPriorityList.length > 0 && addedPriorityList[0].label) {
      nextPage();
    } else {
      alertService.error('Please order priority');
    }
  };

  return (
    <div className="card p-4">
      <div className="row">
        <div className="col-12 text-end">
          <Button
            type="button"
            icon="pi pi-plus"
            label="Add New Priority"
            className="p-button-success mt-2"
            onClick={() => {
              setNewPriority('');
              setPrioritySubmitted(false);
              setPriorityModal(true);
            }}
          />
        </div>
      </div>
      <br />

      <DataTable
        key={`priority-${priority}-${priorityList.length}`}
        value={addedPriorityList}
        responsiveLayout="scroll"
        paginator
        rows={10}
        rowsPerPageOptions={[10, 25, 50]}
      >
        <Column
          header="Priority"
          body={(row: PriorityEntry) =>
            row.label ? (
              <div className="text-wrap">{row.label}</div>
            ) : (
              <Dropdown
                value={priority}
                options={priorityList}
                optionLabel="label"
                optionValue="value"
                placeholder="Select a priority"
                showClear={!!priority}
                appendTo={document.body}
                className="dropdown-fixed"
                panelClassName="dropdown-panel-fit"
                onChange={(e) => setPriority(e.value ?? '')}
              />
            )
          }
        />
        <Column
          header="Action"
          body={(row: PriorityEntry, options) =>
            row.label ? (
              <span>{options.rowIndex + 1}</span>
            ) : (
              <Button
                type="button"
                icon="pi pi-plus"
                label="Add"
                className="p-button-success"
                onClick={() => addOneMorePriority(options.rowIndex)}
              />
            )
          }
        />
        <Column
          header="Remove"
          alignHeader="center"
          body={(row: PriorityEntry, options) =>
            row.label ? (
              <div className="text-center">
                <i
                  className="pi pi-times-circle text-danger cursor-pointer fs-5"
                  role="button"
                  tabIndex={0}
                  onClick={() => removePriorityList(options.rowIndex, row)}
                />
              </div>
            ) : null
          }
        />
      </DataTable>

      <div className="row mt-4">
        <div className="col-6">
          <Button
            type="button"
            icon="pi pi-angle-left"
            label="Previous"
            className="p-button-danger"
            onClick={previousPage}
          />
        </div>
        <div className="col-6 text-end">
          <Button
            type="button"
            icon="pi pi-angle-right"
            iconPos="right"
            label="Next"
            className="p-button-success"
            onClick={prioritySubmit}
          />
        </div>
      </div>

      <Dialog
        header="New Priority"
        modal
        visible={priorityModal}
        draggable={false}
        onHide={() => setPriorityModal(false)}
        headerClassName="dialog-header-secondary"
        resizable={false}
        style={{ width: '40vw' }}
      >
        <div className="row mt-4">
          <div className="col-12">
            <label className="inMandatory">Priority</label>
            <InputText
              value={newPriority}
              placeholder="Enter priority.."
              className={`form-control${
                prioritySubmitted && !newPriority.trim() ? ' is-invalid' : ''
              }`}
              onChange={(e) => setNewPriority(e.target.value)}
            />
            {prioritySubmitted && !newPriority.trim() && (
              <small className="text-danger">Please fill priority field</small>
            )}
          </div>
        </div>
        <div className="row mt-4 pt-2">
          <div className="col-12 text-end">
            <Button
              type="button"
              label="Add"
              className="p-button-success"
              onClick={handleAddNewPriority}
            />
            &nbsp;
            <Button
              type="button"
              label="Cancel"
              className="p-button-danger"
              onClick={() => setPriorityModal(false)}
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default StepPriority;
